import { Button, Form, Tag, Radio } from "antd";
import { useEffect, useState } from "react";
import { file } from "../../API/File";
import { ruleEngine } from "../../API/RuleEngine";
import {
  convertDateInReadableFormat,
  convertPickedDateToUTC,
} from "../../Helper/Methods";
import ReusableForm from "../../unity-ui-common/Components/ReForm";
import ReSelect from "../../unity-ui-common/Components/ReFormFields/ReSelect";
import ReDatePicker from "../../unity-ui-common/Components/ReFormFields/ReDatePicker";
import ReInput from "../../unity-ui-common/Components/ReFormFields/ReInput";
import ReUpload from "../../unity-ui-common/Components/ReFormFields/ReUpload";
import ReRadioGroup from "../../unity-ui-common/Components/ReFormFields/ReRadioGroup";
import { auth } from "../../API/Auth";
import {
  InvestigatorCausalityDropDownItems,
  seriousnessCriteriaDropDownItems,
  severityDropDownItems,
} from "../../Helper/Mappings";
import ReCustomSelect from "../../unity-ui-common/Components/ReCustomSelect/ReCustomSelect";
import { study } from "../../API/UserManagement/Study";
import { product } from "../../API/UserManagement/Product";

interface Iprops {
  initialData?: any;
  onFormSubmit: Function;
  submitButtonText: string;
  type: "version" | "new";
  typeOfImport?: number;
  dataEntryMode?: string;
}

function CreateSafetyDocument(prop: Iprops) {
  const { initialData, onFormSubmit, submitButtonText, type, typeOfImport, dataEntryMode } = prop;
  // In "Manual" mode the sponsor enters Study Number / Product / Study Title freely as text.
  // For every other mode (Centralized, Hybrid) the existing dropdown-driven behaviour is kept.
  const isManual = dataEntryMode === "Manual";
  const [form] = Form.useForm(); // form instance
  const [allCountries, setAllCountries] = useState<any>([]);
  const [studys, setStudys] = useState<any>([]);
 const [products, setProduct] = useState<any>([]);  const [attachmentList, setAttachmentList] = useState<any>([]);
  const [attachmentformData, setAttachmentformData] = useState<any>([]);
  const [submitting, setSubmitting] = useState(false);
  const [documentTypeOtherVisibility, setDocumentTypeOtherVisibility] =
    useState<boolean>(false);
  const [attachmentData, setAttachmentData] = useState<any>([]);

  const getCountries = async () => {
    const res = await ruleEngine.getCountries();
    const resArr = Array.isArray(res) ? res : [];
    const sortedCountries = resArr?.sort((a: any, b: any) => a?.name?.localeCompare(b?.name));
    setAllCountries(sortedCountries);
  };

   const getStudys = async () => {
    const res = await study.getAllStudies();
    setStudys(Array.isArray(res) ? res : []);
  };

   const getProduct = async () => {
    const res = await product.getAllProducts();
    setProduct(Array.isArray(res) ? res : []);
  };

  const handleFormChanges = (changedValues: any, allValues: any) => {
    // When selecting a default 7/15 day type, clear any previously entered typeValue
    if (allValues.type === 7 || allValues.type === 15) {
      form.setFieldValue("typeValue", "");
      setDocumentTypeOtherVisibility(false);
    } else {
      setDocumentTypeOtherVisibility(true);
    }

    // If Other (type === 0) - keep a composed typeValue that follows the pattern:
    // <day> -days <texts> (e.g. "7 -days SUSAR")
    if (allValues.type === 0) {
      const day = allValues.day || "";
      const texts = allValues.texts || "";
      const composed = `${day ? `${day}days` : ""}${texts ? ` ${texts}` : ""}`.trim();
      form.setFieldValue("typeValue", composed);
    }
  // Auto-fill studyTitle when studyID changes (only when Study Number is a dropdown).
  // In Manual mode the user types Study Title themselves, so we must not overwrite it.
     if (!isManual && "studyID" in changedValues) {
    if (!changedValues.studyID) {
      // Study Number was cleared -> clear the auto-filled Study Title too.
      form.setFieldValue("studyTitle", "");
    } else {
      const selectedStudy = studys.find(
        (study: any) => study.studyNumber === changedValues.studyID
      );
      form.setFieldValue("studyTitle", selectedStudy?.studyName || "");
    }
  }
  };

  const handleFormSubmit = async (values: any) => {
    setSubmitting(true);

    // For standard types (7/15), ensure typeValue is empty string. For Other (type=0), compose typeValue
    if (values.type === 0) {
      const day = values.day || "";
      const texts = values.texts || "";
      values["typeValue"] = `${day ? `${day}-days` : ""}${texts ? ` ${texts}` : ""}`.trim();
    } else {
      // For 7/15 day types, make sure typeValue is empty string
      values["typeValue"] = "";
    }

    // Validate we have a type selection
    if (typeof values.type !== "number") return;
  // If 'Other' type selected, require a non-empty typeValue
  if (values.type === 0 && !values.typeValue) return;

    values.attachments = [...attachmentformData, ...attachmentData];
    if (values.lrd) {
      // `values` is handed straight back to the form as its initial values, so
      // `lrd` has to stay a moment here - antd's picker calls date.year() on it.
      // The UTC normalisation happens where the payload is sent instead.
      values["day0"] = convertDateInReadableFormat(
        convertPickedDateToUTC(values.lrd)
      );
    }

    let newSeverityValue: string[] = [];
    values?.severity?.forEach((item: any) => {
      newSeverityValue.push(item.label);
    });
    values["meta"] = {
      saeReportedTerms: values.saeReportedTerm,
      seriousnessCriteria: values.seriousnessCriteria,
      severity: newSeverityValue,
      investigatorCausality: values.investigatorCausality,
      sponsorCausality: values.sponsorCausality,
    };
    values["tags"] = [];
    values["serialNumber"] = "";
    values["org"] = auth.getCurrentWorkspace().id;
    values.creationType = typeOfImport === 0 ? "xml" : "createDocument";
    onFormSubmit(values);
    setSubmitting(false);
  };

  const onBeforeAttachmentUpload = async (fileformData: any) => {
    setSubmitting(true);

    const filenameArr = fileformData["name"].split(".");
    const file_type = filenameArr[filenameArr.length - 1];

    const res = await file.getUrl(file_type);
    const fileName = filenameArr[0];
    setAttachmentList((prev: any) => [...prev, fileformData]);
    await file.remoteUploadFile(res.url, fileformData);
    setAttachmentformData((prev: any) => [
      ...prev,
      {
        fileUID: fileformData.uid,
        id: res.id,
        filename: fileName,
        file_type,
      },
    ]);
    setSubmitting(false);
  };

  const handleAttachmentFileRemove = (fileformData: any) => {
    setSubmitting(true);
    const index = attachmentList.indexOf(fileformData);
    const newFileList = attachmentList.slice();
    newFileList.splice(index, 1);
    setAttachmentList(newFileList);
    setAttachmentformData((prev: any) =>
      prev.filter((item: any) => item.fileUID !== fileformData.uid)
    );
    setSubmitting(false);
  };

  const getAttachmentData = async (attachment: string[]) => {
    const fileData = await Promise.all(
      attachment.map(async (id: string) => {
        const { fileRecord } = await file.getFileData(id);
        return {
          // fileUID: fileRecord.id,
          id: fileRecord.id,
          file_type: fileRecord.file_type,
          filename: fileRecord.filename,
        };
      })
    );
    setAttachmentData(fileData);
  };

  useEffect(() => {
    if (form.getFieldValue("type") === 0) {
      setDocumentTypeOtherVisibility(true);
    } else {
      setDocumentTypeOtherVisibility(false);
    }

    // If there is an initial typeValue for an Other type (type === 0), attempt to parse it into 'day' and 'texts'
    if (initialData?.type === 0 && initialData?.typeValue) {
      const str = initialData.typeValue.trim();
      // Accept patterns like "7 -days Some Text", "7-days Some Text", "7 days Some Text"
      const match = str.match(/^(\d+)\s*-?\s*days\s*(.*)$/i);
      if (match) {
        const [, day, texts] = match;
        form.setFieldsValue({ day: Number(day), texts: texts || "" });
      } else {
        // If it doesn't match the expected pattern, put entire typeValue to texts so a user can edit it.
        form.setFieldsValue({ texts: str });
      }
    }
  }, [initialData]);

  useEffect(() => {
    getCountries();
    getStudys();
    getProduct();
  }, []);

  useEffect(() => {
    if (initialData?.attachment?.length && type === "version") {
      getAttachmentData(initialData?.attachment);
    }
  }, [initialData?.attachment]);

  return (
    <div>
      <ReusableForm
        formInstance={form}
        onChange={handleFormChanges}
        onSubmit={handleFormSubmit}
        initialFormValues={initialData}
      >
        <div
          style={{
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "nowrap",
          }}
        >
          <div>
            <p style={{ marginBottom: "0" }}>
              Safety Document Type
              <label style={{ color: "red", fontSize: "1.2rem" }}>* </label>
            </p>
            <Form.Item name="type" noStyle initialValue={7}>
              <Radio.Group>
                <Radio value={7}>7-day SUSAR</Radio>
                <Radio value={15}>15-day SUSAR</Radio>
                <Radio value={0}>
                  <div style={{ display: "inline-flex", alignItems: "baseline", gap: "0.5rem" }}>
                    <span>Other</span>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "baseline" }}>


                      {/* this is commented to remove the day input field ,that text field will contain day value */}
                      {/* <div style={{ alignSelf: "baseline" }}> */}
                        {/* <ReInput
                          type="number"
                          name="day"
                          label=""
                          required={false}
                          min={0}
                          form={form}
                          width="70px"
                          disable={!documentTypeOtherVisibility}
                        />
                      </div>
                      <div style={{ fontSize: "12px", alignSelf: "baseline" }}>-days</div> */}
                      <ReInput
                        type="simple"
                        name="texts"
                        label=""
                        maxLength={200}
                        required={false}
                        form={form}
                        noStyle
                        width="200px"
                        disable={!documentTypeOtherVisibility}
                      />
                    </div>
                  </div>
                </Radio>
              </Radio.Group>
            </Form.Item>
          </div>
        </div>
        {/* <ReInput
          label="Study Number"
          name="studyID"
          type="simple"
          required={!documentTypeOtherVisibility}
        /> */}

        {isManual ? (
          <ReInput
            label="Study Number"
            form={form}
            name="studyID"
            type="simple"
            required={false}
          />
        ) : (
          <ReSelect
            label="Study Number"
            form={form}
            // type="multiple"
            name="studyID"
            items={studys.map((inv: any) => {
              return {
                value: inv.studyNumber,
                title: inv.studyNumber,
              };
            })}
            //  required={!documentTypeOtherVisibility}
            required={false}
          />
        )}

        {isManual ? (
          <ReInput
            label="Product"
            form={form}
            name="product"
            type="simple"
            required={false}
          />
        ) : (
          <ReSelect
            label="Product"
            form={form}
            // type="multiple"
            name="product"
            items={products.map((inv: any) => {
              return {
                value: inv.name,
                title: inv.name,
              };
            })}
            //  required={!documentTypeOtherVisibility}
            required={false}
          />
        )}

        <ReInput
          label="Study Title"
          //  required={!documentTypeOtherVisibility}
          required={false}
          form={form}
          name="studyTitle"
          type="simple"
          disable={!isManual}
        />
        <ReInput
          label="Site Number"
          name="sourceSiteID"
          type="simple"
          //  required={!documentTypeOtherVisibility}
          required={false}
          form={form}
        />
        <ReInput
          name="sourceSiteName"
          label="Site Name"
          type="simple"
          //  required={!documentTypeOtherVisibility}
          required={false}
          form={form}
        />
        <ReCustomSelect
          formInstance={form}
          label="Severity"
          name="severity"
          //  required={!documentTypeOtherVisibility}
          required={false}
          dropDownItems={severityDropDownItems?.map((ele: any) => {
            return {
              key: ele.title,
              label: ele.title,
            };
          })}
          defaultValues={
            type === "new"
              ? form.getFieldValue("severity") || []
              : initialData?.severity
          }
        />
        <ReDatePicker
          label="Day 0"
          name="lrd"
          dateFormat="DD-MMM-YYYY"
          featureDates
          form={form}
          type="simple"
          width="100%"
          //  required={!documentTypeOtherVisibility}
          required={false}
        />
        <ReInput
          label="Safety Document ID"
          form={form}
          name="safetyDatabaseReportNumber"
          type="simple"
          //  required={!documentTypeOtherVisibility}
          required={false}
        />
        <ReSelect
          label="Reporter Causality"
          form={form}
          type="multiple"
          name="investigatorCausality"
          items={InvestigatorCausalityDropDownItems.map((inv: any) => {
            return {
              value: inv.title,
              title: inv.title,
            };
          })}
          //  required={!documentTypeOtherVisibility}
          required={false}
        />
        <ReSelect
          label="Company Causality"
          form={form}
          type="multiple"
          name="sponsorCausality"
          items={InvestigatorCausalityDropDownItems.map((inv: any) => {
            return {
              value: inv.title,
              title: inv.title,
            };
          })}
          //  required={!documentTypeOtherVisibility}
          required={false}
        />
        <ReSelect
          form={form}
          type="multiple"
          label="Seriousness Criteria"
          name="seriousnessCriteria"
          items={seriousnessCriteriaDropDownItems.map((ser: any) => {
            return {
              value: ser.key,
              title: ser.title,
            };
          })}
          //  required={!documentTypeOtherVisibility}
          required={false}
        />
        <ReSelect
          label="Country"
          name="country"
          form={form}
          searchable
          
          items={allCountries.map((cou: any) => {
            return {
              value: cou.alpha2,
              title: cou.name,
            };
          })}
          //  required={!documentTypeOtherVisibility}
          required={false}
        />
        <ReInput
          label="SAE Reported Term"
          name="saeReportedTerm"
          form={form}
          type="simple"
          //  required={!documentTypeOtherVisibility}
          required={false}
        />
        {type === "version" && (
          <div
            style={{
              margin: "0.5rem 0",
              display: "flex",
              flexWrap: "wrap",
              rowGap: "0.5rem",
            }}
          >
            {attachmentData.map((ele: any) => {
              return (
                <Tag
                  closable
                  onClose={() => {
                    setAttachmentData(
                      attachmentData.filter((att: any) => att.id !== ele.id)
                    );
                  }}
                  key={ele.id}
                >
                  {ele.filename}
                </Tag>
              );
            })}
          </div>
        )}

        <ReUpload
          errorMessage="Please enter your file"
          accept=".pdf"
          label="Attach File"
          BtnTitle="Click to Upload File"
          name="attachments"
          onBeforeUpload={onBeforeAttachmentUpload}
          onRemove={handleAttachmentFileRemove}
          fileListMaxCount={8}
          fileList={attachmentList}
          multiple
        />
        <Form.Item noStyle>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {submitButtonText}
          </Button>
        </Form.Item>
      </ReusableForm>
    </div>
  );
}

export default CreateSafetyDocument;
